import {
  computed,
  observable,
  action,
} from 'mobx';
import { RouterStore } from '@estee/elc-router';
import TransactionRepository from '../../repositories/TransactionRepository/TransactionRepository';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';
import ConfigRepository from '../../repositories/ConfigRepository';
import SavePrivacyPolicyUseCase from '../../use-cases/SavePrivacyPolicyUseCase';
import Config from '../../domain/entities/Config/Config';
import Payment from '../../domain/entities/Payment/Payment';
import PAYMENT_PROVIDERS, { PAYMENT_MODES } from '../../constants/PaymentProviders';
import PaymentService from '../../application/services/PaymentService';
import UIState from '../../stores/UIState';
import GiftCardUIState from '../../stores/UIState/GiftCardUIState';
import STATUS from '../../constants/Status';
import Transaction from '../../domain/entities/Transaction/Transaction';
import ShippingRepository from '../../repositories/ShippingRepository';
import {
  IAcceptedCreditCards,
  IAcceptedInstallments,
} from '../../api/IApiResponses';
import UserPreferences from '../../domain/entities/UserPreferences/UserPreferences';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import Order from '../../domain/entities/Order/Order';
import formatPrice from '../../utils/PriceFormatUtils';
import { showNonKoreanShippingWarning } from '../../utils/KoreanAddressUtils';

import BopisStoresRepository from '../../repositories/BopisStoresRepository';
import { DeliveryOptions } from '../../constants/Constants';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';
import { PaymentRepository } from '../../repositories/PaymentRepository/PaymentRepository';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE, SHIPPING_PAYMENT_NOTIFICATIONS } from '../../constants/Notifications';
import { isKorea } from '../../utils/CountryUtils';

interface IPaymentConfig {
  analyticsRepository: AnalyticsRepository;
  userPreferences: UserPreferences;
  configRepository: ConfigRepository;
  paymentService: PaymentService;
  uiState: UIState;
  giftCardUIState: GiftCardUIState;
  shippingRepository: ShippingRepository;
  transactionRepository: TransactionRepository;
  bopisStoresRepository: BopisStoresRepository;
  stateRouter: RouterStore;
  notificationsCollection: NotificationsCollection;
  paymentRepository: PaymentRepository;
}

interface IPaymentScreenViewControllerData {
  acceptedInstallments: IAcceptedInstallments[];
  checkoutWithoutPayment: boolean;
  isAfterpayEligible: boolean;
  isMobile: boolean;
  isPaymentLoading: boolean;
  isTransactionLoaded: boolean;
  isBopisEnabled: boolean;
  locale: string;
  selectedPaymentMode: PAYMENT_MODES;
  showErrorMessages: boolean;
  showNotificationMessages: boolean;
  showShippingNotifications: boolean;
  showContinueButton: boolean;
  requirePrivacyPolicy: boolean;
  hasMultiplePaymentMethods: boolean;
  hasPaymentInstructions: boolean;
  advancedLegal: boolean;
  isSetDefaultCardOptionVisible: boolean;
  isSaveCardOptionVisible: boolean;
  deliveryOption: DeliveryOptions;
  showCharityDonation: boolean;
  showWalletPayment: boolean;
  showInvoiceCard: boolean;
  skipBillingAddress: boolean;
}

export default class PaymentScreenViewController {
  transactionRepository: TransactionRepository;

  analyticsRepository: AnalyticsRepository;

  userPreferences: UserPreferences;

  configRepository: ConfigRepository;

  paymentService: PaymentService;

  stateRouter: RouterStore;

  @observable config: Config;

  @observable uiState: UIState;

  @observable giftCardUIState: GiftCardUIState;

  @observable transaction: Transaction;

  @observable payment: Payment;

  @observable orderSummary: Order;

  @observable shippingRepository: ShippingRepository;

  @observable bopisStoresRepository: BopisStoresRepository;

  @observable notificationsCollection: NotificationsCollection;

  @observable isCCExpired = false;

  paymentRepository: PaymentRepository;

  constructor(config: IPaymentConfig) {
    this.transactionRepository = config.transactionRepository;
    this.analyticsRepository = config.analyticsRepository;
    this.userPreferences = config.userPreferences;
    this.configRepository = config.configRepository;
    this.paymentService = config.paymentService;
    this.uiState = config.uiState;
    this.shippingRepository = config.shippingRepository;
    this.bopisStoresRepository = config.bopisStoresRepository;
    this.stateRouter = config.stateRouter;
    this.notificationsCollection = config.notificationsCollection;
    this.paymentRepository = config.paymentRepository;
    this.giftCardUIState = config.giftCardUIState;
    this.config = this.configRepository.config;
  }

  @computed
  get data(): IPaymentScreenViewControllerData {
    return {
      acceptedInstallments: this.transaction && this.transaction.acceptedInstallments,
      advancedLegal: this.config && this.config.advancedLegal,
      checkoutWithoutPayment: this.transaction
        && this.transaction.payment && this.transaction.payment.checkoutWithoutPayment,
      deliveryOption: this.transaction && this.transaction.deliveryOption,
      hasMultiplePaymentMethods: this.payment && this.payment.hasMultiplePaymentMethods,
      hasPaymentInstructions: this.config && this.config.paymentInstructions,
      isAfterpayEligible: this.transaction && this.transaction.isAfterpayEligible,
      isBopisEnabled: this.config && this.config.bopisEnabled,
      isMobile: this.uiState.isMobile,
      isPaymentLoading: this.isPaymentLoading,
      isSaveCardOptionVisible: this.isSaveCardOptionVisible,
      isSetDefaultCardOptionVisible: this.isSetDefaultCardOptionVisible,
      isTransactionLoaded: this.isTransactionLoaded,
      locale: this.config && this.config.locale,
      requirePrivacyPolicy: this.userPreferences && this.userPreferences.requirePrivacyPolicy,
      selectedPaymentMode: this.selectedPaymentMode,
      showCharityDonation: this.config?.isCharityDonationEnabled && !this.config?.isGiftWrapAndDonationsHidingExperimentEnabled,
      showContinueButton: this.showContinueButton,
      showErrorMessages: this.showErrorMessages,
      showInvoiceCard: !!this.config?.showInvoiceSection,
      showNotificationMessages: this.showNotificationMessages,
      showShippingNotifications: this.showShippingNotifications,
      showWalletPayment: this.config?.isWalletPaymentEnabled && !this.payment?.isPayByLink,
      skipBillingAddress: !!this.config?.skipBillingAddress,
    };
  }

  @action
  load = async (): Promise<void> => {
    const config = await this.configRepository.getConfig();
    const transaction = await this.transactionRepository.getTransaction();
    const {
      orderSummary,
      payment,
    } = transaction;
    this.setConfig(config);
    this.setTransaction(transaction);
    this.setOrderSummary(orderSummary);
    this.setPayment(payment);
    this.userPreferences.setAcceptedPrivacyPolicy(this.config.defaultTermsConditions);

    if (isKorea(this.config?.countryId ?? 0)) {
      this.updateNonKoreanShippingNotification();
    }
  };

  private updateNonKoreanShippingNotification = (): void => {
    const key = SHIPPING_PAYMENT_NOTIFICATIONS.NON_KOREAN_SHIPPING;

    this.notificationsCollection.clearNotificationByKey(key);
    
    if (showNonKoreanShippingWarning(this.transaction?.shippingAddress?.attributes, this.config?.countryId)) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.SHIPPING_PAYMENT,
        key,
        type: NOTIFICATION_TYPE.WARNING,
      });
    }
  };

  @action
  setConfig = (config: Config): void => {
    this.config = config;
  };

  @action
  setTransaction = (transaction: Transaction): void => {
    this.transaction = transaction;
  };

  @action
  setOrderSummary = (orderSummary: Order): void => {
    this.orderSummary = orderSummary;
  };

  @action
  setPayment = (payment: Payment): void => {
    this.payment = payment;
  };

  initPayment = async (): Promise<void> => {
    this.paymentRepository.setupSelectedPayment();
  };

  // This is for the new payment methods strategy implementation
  onPaymentMethodChange = (): void => {
    this.notificationsCollection.clearNotifications(undefined, NOTIFICATION_TYPE.DANGER);
    this.giftCardUIState.resetGiftCardMessages();
  };

  getPrice = (price: number): string => this.config && formatPrice(this.config, price);

  @computed
  get showNotificationMessages(): boolean {
    return this.notificationsCollection?.hasNotificationsByGroup(undefined, NOTIFICATION_TYPE.WARNING)
            && !this.showShippingNotifications;
  }

  @computed
  get showShippingNotifications(): boolean {
    return this.notificationsCollection?.hasNotificationsByGroup(NOTIFICATION_GROUP.SHIPPING_PAYMENT);
  }

  @computed
  get showErrorMessages(): boolean {
    return !this.isPaymentLoading && this.notificationsCollection?.hasErrors;
  }

  getAcceptedCreditCards = (paymentMethod: PAYMENT_PROVIDERS, paymentMode?: PAYMENT_MODES):
  IAcceptedCreditCards[] => this.payment.getAcceptedCreditCards(paymentMethod, paymentMode);

  onContinuePay = (payloadData: {}): void => {
    this.paymentService.pay(payloadData);
  };

  @computed get selectedPaymentMethod(): PAYMENT_PROVIDERS {
    return this.paymentService?.paymentProvider;
  }

  @computed get selectedPaymentMode(): PAYMENT_MODES {
    return this.paymentService?.paymentMode;
  }

  @computed
  get isPaymentLoading(): boolean {
    return this.paymentService.paymentStatus === STATUS.LOADING;
  }

  @computed
  get showContinueButton(): boolean {
    return (
      this.isPaymentMethodSelected(PAYMENT_PROVIDERS.ADYEN_WEB)
        && (this.payment?.isPayByLink)
    );
  }

  isPaymentMethodSelected =
  (paymentMethod: PAYMENT_PROVIDERS): boolean => paymentMethod === this.selectedPaymentMethod;

  isPaymentModeSelected =
  (paymentMode: PAYMENT_MODES): boolean => paymentMode === this.selectedPaymentMode;

  paymentScreenViewEvent = (): void => {
    this.analyticsRepository.paymentScreenViewEvent();
  };

  @computed
  get isTransactionLoaded(): boolean {
    return Boolean(this.transaction && this.transaction.transId);
  }

  setAcceptedPrivacyPolicy(value: boolean): void {
    const useCase = new SavePrivacyPolicyUseCase(value);
    useCase.execute();
  }

  payWithGiftCard = async (): Promise<void> => {
    try {
      await this.paymentService.paymentStrategy.payWithGiftCard();
    } catch (error) {
      const normalizedErrors = normalizeApiErrors(error, NOTIFICATION_GROUP.PAYMENT);
      this.notificationsCollection.addNotifications(normalizedErrors);
      await this.initPayment();
    }
  };

  @computed
  private get isSetDefaultCardOptionVisible(): boolean {
    return !this.transaction?.isGuestOrPOA;
  }

  @computed
  private get isSaveCardOptionVisible(): boolean {
    return !this.transaction?.isGuestOrPOA;
  }

  isSelectedCCExpired = (selectedId: string): boolean => {
    // Only check for saved cards that have an ID and not if new card option is selected.
    if (this.transaction && Number(selectedId)) {
      const selectedCreditCard = this.transaction.getCardByCCUserPaymentId(selectedId);
      return selectedCreditCard.isExpired;
    }
    return false;
  };

  // To set CC expired error on Payment screen.
  setExpiredOnSelect = (selectedId: string): void => {
    if (this.isSelectedCCExpired(selectedId)) {
      this.isCCExpired = true;
    } else {
      this.isCCExpired = false;
    }
  };
}
